import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild, AfterViewChecked, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";

import { LgdFormControl, LgdFormControlBaseConfig, LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { TreeNode } from "@tylertech/tyler-api-types";

import { ExpandableTreeNode, NavigationService, NavMenuService, RouteInfoService } from "../../services";
import { debounceTime } from "rxjs";

@Component({
    selector: "app-menu",
    templateUrl: "./appMenu.tmpl.html",
    styleUrls: [
        "./appMenu.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppMenuComponent
{
    private changeDetectorRef = inject(ChangeDetectorRef);
    private readonly $navMenuService = inject(NavMenuService);
    private readonly $navigationService = inject(NavigationService);
    private $routeInfoService = inject(RouteInfoService);

    private readonly log = getLoggerFor("AppMenuComponent");
    readonly formGroup: FormGroup;

    protected data: ReadonlyArray<ExpandableTreeNode>;

    isLoaded: boolean;
    selectedNode: TreeNode;
    dataSource: ExpandableTreeNode;
    filterString: string;
    busy = false;
    private _navPath: string;
    protected initialLoad = true;
    navMenuOpen: boolean = false;

    @ViewChild('treeMenu', { read: ElementRef }) treeMenu: ElementRef/* <TreeComponent> */;

    constructor()
    {
        this.formGroup = new FormGroup({
            "filter": LgdFormControlFactory.text()
        });

        this.$navMenuService.menuChange
            .pipe(takeUntilDestroyed())
            .subscribe((newMenu) =>
            {
                this.log.debug(`navMenuService.menuChange! Reloading the main menu.`);
                this.data = newMenu;

                this.updateMenu(true);

                this.isLoaded = this.dataSource.Children?.length > 0;
                this.changeDetectorRef.markForCheck();
            });

        this.$navMenuService.toggleChange
            .pipe(takeUntilDestroyed())
            .subscribe((open: boolean) =>
            {
                this.log.debug(`navMenuService.toggleChange!`);
                if (open)
                {
                    if (!this.initialLoad || this.isNavPinned === false)
                    {
                        this.filterControl.focus();
                    }
                    this.initialLoad = false;
                }

                this.navMenuOpen = open;
                this.changeDetectorRef.markForCheck();
            });

        this.filterControl.valueChanges
            .pipe(
                debounceTime(100),
                takeUntilDestroyed())
            .subscribe((newValue: string) =>
            {
                this.filterString = newValue;
                if (!this.filterString)
                {
                    this.busy = true;
                    this.changeDetectorRef.detectChanges();
                }

                this.updateMenu(false);
            });
    }

    protected get filterControl(): LgdFormControl<LgdFormControlBaseConfig, string> { return this.formGroup.get("filter") as LgdFormControl<LgdFormControlBaseConfig, string>; }

    private get navPath(): string
    {
        if (!this._navPath)
        {
            const path = this.$routeInfoService.path + window.location.search;
            if (path)
            {
                this._navPath = decodeURIComponent(path.substring(path.indexOf("/apps/") + 1)
                    .replace("//", "/"));
                if (this._navPath.substring(this._navPath.length - 1) === "/")
                {
                    this._navPath = this._navPath.substring(0, this._navPath.length - 1);
                }
            }
        }
        return this._navPath;
    }

    get navPinIcon(): string
    {
        return this.isNavPinned ? "pin_off_outline" : "pin_outline";
    }

    get isNavPinned(): boolean
    {
        return this.$navMenuService.IsNavPinned;
    }

    get navPinText(): string
    {
        return `${this.isNavPinned ? "Unpin" : "Pin"} navigation menu`;
    }

    searchKeyUp(event: KeyboardEvent): void
    {
        if (this.filterControl.value === ""
            && event.key !== "F2"
            && event.key !== "Control")
        {
            this.busy = true;
        }
    }

    private resetExpanded(node: ExpandableTreeNode): void
    {
        if (node.TreePath !== this.selectedNode.TreePath.split("/")[0])
        {
            node.$expanded = false;
        }

        (node.Children as ExpandableTreeNode[]).forEach(x =>
        {
            x.$expanded = false;

            if (x.Children?.length > 0)
            {
                this.resetExpanded(x);
            }
        });
    }

    @LogInvocation(LogLevel.DEBUG)
    protected updateMenu(newRoute: boolean): void
    {
        if (newRoute)
        {
            this._navPath = undefined;
            this.selectedNode = undefined;
        }

        if (!this.data)
        {
            this.log.debug(`updateMenu(): No data`);
            this.dataSource = { Key: "$root", TreePath: "/", Children: [] };
            return;
        }

        if (this.filterString)
        {
            this.dataSource = { Key: "$root", TreePath: "/", Children: this.buildFilteredTree(this.data, this.filterString) };
        }
        else
        {
            const self = this;
            this.dataSource = { Key: "$root", TreePath: "/", Children: [...this.data] };

            const path = this.navPath.toLowerCase().split("&$filter")[0];
            if (path)
            {
                this.dataSource.Children.map(this.copy)
                    .filter(function f(o: ExpandableTreeNode)
                    {
                        const exec = o.Execute
                            ? o.Execute?.split("&$filter")[0].substring(o.Execute.indexOf("/apps/") + 1)
                                .replace("//", "/")
                                .replace(/\+/g, " ")
                                .toLowerCase()
                            : "";
                        if (decodeURIComponent(exec) === path)
                        {
                            if (self.selectedNode)
                            {
                                return false;
                            }
                            self.selectedNode = o;
                            return true;
                        }
                        if (o.Children)
                        {
                            let children = o.Children.map(self.copy);
                            return (children = children.filter(f)).length;
                        }
                    });


                if (newRoute && this.selectedNode)
                    this.dataSource.Children.forEach(n => this.$navMenuService.updateNodes(n, this.selectedNode));
            }
        }

        this.busy = false;
        this.changeDetectorRef.markForCheck();
    }

    private copy<T>(o: T): T
    {
        return Object.assign({}, o);
    }

    @LogInvocation(LogLevel.DEBUG)
    private buildFilteredTree(arr: ReadonlyArray<TreeNode>, text: string): Array<TreeNode>
    {
        const matches: Array<TreeNode> = [];
        if (!Array.isArray(arr))
        {
            return matches;
        }

        arr.forEach((n: TreeNode) =>
        {
            if (n.Children?.length > 0)
            {
                const childResults = this.buildFilteredTree(n.Children, text);
                if (childResults.length)
                {
                    matches.push(Object.assign({}, n, { Children: childResults }));
                }
            }

            if (!n.Children && n.Key.toUpperCase().indexOf(text.toUpperCase()) !== -1)
            {
                const depth = (n.TreePath.match(/\//g) || []).length;
                const offset = (depth - 1) * 3;
                n.ShowTooltip = n.Key.length > (30 - offset);
                matches.push(n);
            }
        });
        return matches;
    }

    clearSearch(): void
    {
        this.filterControl.setValue(undefined);

        (this.dataSource?.Children as ExpandableTreeNode[])
            ?.forEach(x =>
            {
                if (x.$expanded === true)
                {
                    return;
                }

                this.resetExpanded(x);
            });
    }

    toggleNavPin(): void
    {
        this.$navMenuService.setMenuOpen(!this.$navMenuService.IsNavPinned);
    }

    onSelect($event: TreeNode): void
    {
        this.selectedNode = $event;
    }
}
